import { Flex, Spinner, Text } from '@chakra-ui/react';

import { useQuery } from '@tanstack/react-query';

import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { ButtonText } from '@morressier/alembic';
import { Layout } from 'src/components/Layout';
import { Overlay } from 'src/components/Overlay';
import { Preview } from '../../ActivityTab/EmailViewModal';

import { formatDateAndTime } from 'src/utils/date';

import { fetchEmailByEventId } from 'src/api/queries';

import { type IEmailLog } from '@publishing-engine/domain/types/email-template';
import { IRoleType } from '@publishing-engine/domain/models';

interface IEmailLogHistoryModalProps {
  onClose: () => void;
  emailLog: IEmailLog;
  fullName: string;
  email: string;
  roleContext?: IRoleType;
}

export default function EmailLogHistoryModal({
  onClose,
  emailLog,
  fullName,
  email,
  roleContext
}: IEmailLogHistoryModalProps) {
  const cretaedAt = formatDateAndTime(emailLog.createdAt);

  const {
    data: emailLogData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['email-by-event', emailLog.eventId, roleContext, email],
    queryFn: () =>
      fetchEmailByEventId(
        emailLog.submissionId,
        emailLog.eventId,
        roleContext,
        email
      ),
    enabled: !!emailLog.eventId && !!email
  });

  let content: React.ReactNode;

  if (isLoading) {
    content = (
      <Flex alignItems="center" justifyContent="center" height="100%">
        <Spinner />
      </Flex>
    );
  }

  if (isError) {
    content = (
      <Text color="red.500">
        Error loading email:{' '}
        {error instanceof Error ? error.message : 'Unknown error'}
      </Text>
    );
  }

  if (emailLogData) {
    content = <Preview emailLog={emailLogData?.items} />;
  }

  return (
    <Overlay>
      <Flex direction="column" backgroundColor="surfaceSecondaryFill">
        <Layout width="800px">
          <Flex direction="column" gap={6}>
            <Flex justifyContent="space-between" alignItems="center">
              <ButtonText
                variant="secondary"
                onClick={onClose}
                iconLeft={<FontAwesomeIcon icon={faArrowLeft} />}
              >
                Back to Email Log
              </ButtonText>
            </Flex>

            <Flex direction="column" flex={1}>
              <Text textStyle="headingSection">
                Submission associated with {fullName}
              </Text>
              <Flex gap={4} mt={2}>
                <Text textStyle="textBody">By {fullName}</Text>
                <Text textStyle="textBody">{cretaedAt}</Text>
              </Flex>
            </Flex>

            {content}
          </Flex>
        </Layout>
      </Flex>
    </Overlay>
  );
}
